import { GamIntegration, GamNetworkConfig } from '@pubstack/common/src/gam/adx'
import { AdminScope } from '@pubstack/common/src/scope'
import { useQueryClient } from '@tanstack/react-query'
import { FunctionComponent, useState } from 'react'
import { useCreateGamNetwork, useUpdateGamNetwork } from '~/api/admin/admin-gam-networks.api'
import { GAM_INTEGRATION_QUERY_KEYS, useCreateServiceAccount, useLaunchGamChecks } from '~/api/admin/gam-integration.api'
import { PureAdminScopeFeaturesGamIntegration } from './PureAdminScopeFeaturesGamIntegration'

type AdminScopeFeaturesGamIntegrationProps = {
  scope: AdminScope
  currentIntegration: GamIntegration
  gamIntegrationLoading: boolean
}
export const AdminScopeFeaturesGamIntegration: FunctionComponent<AdminScopeFeaturesGamIntegrationProps> = ({ scope, currentIntegration, gamIntegrationLoading }) => {
  const [selectedNetworkCode, setSelectedNetworkCode] = useState<string>()
  const queryClient = useQueryClient()
  const queryKey = [...GAM_INTEGRATION_QUERY_KEYS, scope.id ?? '']

  const { mutateAsync: createServiceAccountMutation, isPending: isServiceAccountPending } = useCreateServiceAccount(scope.id ?? '')
  const { mutateAsync: createNetworkMutation, isPending: isCreateNetworkPending } = useCreateGamNetwork(scope.id ?? '')
  const { mutateAsync: updateNetworkMutation, isPending: isUpdateNetworkPending } = useUpdateGamNetwork(scope.id ?? '', selectedNetworkCode ?? '')
  const { mutateAsync: launchChecksMutation, isPending: isLaunchChecksPending } = useLaunchGamChecks(scope.id ?? '')

  const updateIntegrationCache = (updater: (integration: GamIntegration) => GamIntegration) => {
    queryClient.setQueryData<GamIntegration>(queryKey, (old) => (old ? updater(old) : old))
  }

  const launchGamChecks = async (networkCode: number) => {
    const network = await launchChecksMutation({ networkCode })
    updateIntegrationCache((integration) => ({
      ...integration,
      gamNetworks: integration.gamNetworks?.map((n) => (n.networkCode === networkCode.toString() ? network : n)) ?? [],
    }))
  }

  const createServiceAccount = async () => {
    await createServiceAccountMutation({ scopeName: scope.name })
    await queryClient.invalidateQueries({ queryKey })
  }

  const createNetwork = async (network: GamNetworkConfig) => {
    if (scope) {
      const newNetwork = await createNetworkMutation({ networkCode: network.networkCode, scopeName: scope.name })
      updateIntegrationCache((integration) => ({
        ...integration,
        gamNetworks: [...(integration.gamNetworks ?? []), newNetwork],
      }))
    }
  }

  const updateNetwork = async (network: GamNetworkConfig) => {
    if (scope) {
      setSelectedNetworkCode(network.networkCode)
      const updatedNetwork = await updateNetworkMutation(network)
      if (updatedNetwork) {
        updateIntegrationCache((integration) => ({
          ...integration,
          gamNetworks: integration.gamNetworks?.map((n) => (n.networkCode === network.networkCode ? updatedNetwork : n)) ?? [],
        }))
      }
    }
  }
  const startAdxIngestionTesting = (network: GamNetworkConfig) => {
    if (network.dataIngestionStatus === 'INITIALIZED') {
      const updatedNetwork: GamNetworkConfig = { ...network, dataIngestionStatus: 'TESTING' }
      updateNetwork(updatedNetwork)
    }
  }

  const toggleAdxIngestion = (network: GamNetworkConfig) => {
    const shouldActivate = network.dataIngestionStatus === 'ERROR' || network.dataIngestionStatus === 'INITIALIZED' || network.dataIngestionStatus === 'TESTING'
    const updatedNetwork: GamNetworkConfig = { ...network, dataIngestionStatus: shouldActivate ? 'ACTIVE' : 'STOPPED', openBiddingEnabled: false, gamIntegrationEnabled: false }
    updateNetwork(updatedNetwork)
  }

  const toggleOpenBiddingIngestion = (network: GamNetworkConfig) => {
    const updatedNetwork: GamNetworkConfig = { ...network, openBiddingEnabled: !network.openBiddingEnabled }
    updateNetwork(updatedNetwork)
  }

  const toggleGamIntegrationIngestion = (network: GamNetworkConfig) => {
    const updatedNetwork: GamNetworkConfig = { ...network, gamIntegrationEnabled: !network.gamIntegrationEnabled }
    updateNetwork(updatedNetwork)
  }

  const isNetworkLoading = isCreateNetworkPending || isUpdateNetworkPending

  //TODO tmu nra 2022-09-21 -- handle error
  return (
    <PureAdminScopeFeaturesGamIntegration
      loading={{ isIntegrationLoading: gamIntegrationLoading || isServiceAccountPending, isCheckLoading: isLaunchChecksPending, isNetworkLoading }}
      integration={currentIntegration}
      createGamNetwork={createNetwork}
      createServiceAccount={createServiceAccount}
      launchChecks={launchGamChecks}
      toggleOpenBiddingIngestion={toggleOpenBiddingIngestion}
      toggleAdxIngestion={toggleAdxIngestion}
      toggleGamIntegrationIngestion={toggleGamIntegrationIngestion}
      startAdxIngestionTesting={startAdxIngestionTesting}
    />
  )
}
